import { alpha, Box, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Avatar } from 'components/ui';

const HomeRoot = styled(Box)({
  boxSizing: 'border-box',
  padding: '24px 16px',
  width: '100%',
});

const FirmRibbon = styled(Box)({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: 8,
  marginBottom: 32,
  overflowX: 'auto',
  paddingBottom: 4,
});

const FirmIconWrapper = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'flex',
  flexShrink: 0,
  height: 44,
  justifyContent: 'center',
  overflow: 'hidden',
  transition: 'box-shadow 140ms ease',
  width: 44,
  '&:hover': {
    boxShadow: theme.shadows[3],
  },
}));

const FirmIconAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: 8,
  color: theme.palette.common.white,
  fontSize: 12,
  fontWeight: 700,
  height: 44,
  width: 44,
  // Avatar from components/ui is a styled MuiAvatar — override variant inline
  '&.MuiAvatar-root': {
    borderRadius: 8,
  },
}));

interface FeatureCardStyleProps {
  $bgColor: string;
}

const FeatureGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(2, 1fr)',
  marginBottom: 32,
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

const FeatureCard = styled(Card, {
  shouldForwardProp: prop => prop !== '$bgColor',
})<FeatureCardStyleProps>(({ $bgColor, theme }) => ({
  backgroundColor: $bgColor,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
}));

const CompanyHeading = styled(Box)({
  alignItems: 'baseline',
  display: 'flex',
  gap: 12,
  marginBottom: 24,
});

const CompanyListRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const LetterGroup = styled(Box)({
  marginBottom: 16,
});

const LetterHeaderBox = styled(Box)({
  marginBottom: 8,
});

const CompanyGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  },
  [theme.breakpoints.up('xl')]: {
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  },
}));

const CompanyCell = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  cursor: 'pointer',
  padding: '12px 16px',
  transition: 'background-color 100ms ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '& .company-name': {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },
}));

export {
  CompanyCell,
  CompanyGrid,
  CompanyHeading,
  CompanyListRoot,
  FeatureCard,
  FeatureGrid,
  FirmIconAvatar,
  FirmIconWrapper,
  FirmRibbon,
  HomeRoot,
  LetterGroup,
  LetterHeaderBox,
};
